import { Component, OnInit } from '@angular/core';
import { deleteDialog } from 'src/app/shared/dialog';
import { ADAPTER_TYPES, AdapterType, AGENT_PERMISSIONS, Provider, ProviderRequest } from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';
import { blankToNull } from '../../utils/clipboard';

interface ProviderForm {
  code: string;
  displayName: string;
  adapterType: AdapterType;
  baseUrl: string;
  enabled: boolean;
}

@Component({
  selector: 'app-ai-providers-page',
  templateUrl: './providers-page.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class ProvidersPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  readonly adapterTypes = ADAPTER_TYPES;

  providers: Provider[] = [];
  loading = false;

  editorOpen = false;
  editingId: string | null = null;
  form: ProviderForm = this.blankForm();
  formError = '';

  keyDialogFor: Provider | null = null;
  apiKey = '';
  showKey = false;

  constructor(private api: AiAgentsApiService, public perms: AgentPermissionsService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.listProviders().subscribe({
      next: (list) => {
        this.providers = list || [];
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  openCreate(): void {
    this.editingId = null;
    this.form = this.blankForm();
    this.formError = '';
    this.editorOpen = true;
  }

  openEdit(p: Provider): void {
    this.editingId = p.id;
    this.form = {
      code: p.code,
      displayName: p.displayName,
      adapterType: p.adapterType,
      baseUrl: p.baseUrl,
      enabled: p.enabled,
    };
    this.formError = '';
    this.editorOpen = true;
  }

  closeEditor(): void {
    this.editorOpen = false;
  }

  save(): void {
    const body: ProviderRequest = {
      code: blankToNull(this.form.code)?.toUpperCase() ?? null,
      displayName: blankToNull(this.form.displayName),
      adapterType: this.form.adapterType,
      baseUrl: blankToNull(this.form.baseUrl),
      enabled: this.form.enabled,
    };
    if (!body.code || !body.displayName || !body.baseUrl) {
      this.formError = 'Code, display name and base URL are required.';
      return;
    }
    const call = this.editingId ? this.api.updateProvider(this.editingId, body) : this.api.createProvider(body);
    call.subscribe({
      next: () => {
        this.editorOpen = false;
        this.load();
      },
      error: (err) => (this.formError = err?.error?.message || 'Saving failed.'),
    });
  }

  openKeyDialog(p: Provider): void {
    this.keyDialogFor = p;
    this.apiKey = '';
    this.showKey = false;
  }

  closeKeyDialog(): void {
    this.keyDialogFor = null;
    this.apiKey = '';
  }

  saveKey(): void {
    const provider = this.keyDialogFor;
    const key = this.apiKey.trim();
    if (!provider || !key) {
      return;
    }
    this.api.setProviderApiKey(provider.id, key).subscribe({
      next: (updated) => {
        this.apiKey = '';
        this.keyDialogFor = null;
        this.providers = this.providers.map((x) => (x.id === updated.id ? updated : x));
      },
    });
  }

  toggleEnabled(p: Provider): void {
    this.api.updateProvider(p.id, { enabled: !p.enabled }).subscribe({
      next: (updated) => (this.providers = this.providers.map((x) => (x.id === updated.id ? updated : x))),
    });
  }

  async remove(p: Provider): Promise<void> {
    const confirm = await deleteDialog();
    if (!confirm.isConfirmed) {
      return;
    }
    this.api.deleteProvider(p.id).subscribe({ next: () => this.load() });
  }

  private blankForm(): ProviderForm {
    return { code: '', displayName: '', adapterType: 'OPENAI_COMPATIBLE', baseUrl: '', enabled: true };
  }
}
